import React from 'react';
import { Search, Grid, List, X } from 'lucide-react';

// A simple Tailwind-styled Badge component
const Badge = ({ children, className, onClick }) => (
  <span
    onClick={onClick}
    className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${className}`}
  >
    {children}
  </span>
);

// A simple Tailwind-styled Button component
const Button = ({ variant, size, onClick, children, className }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
      ${size === 'icon' ? 'w-12 h-12' : 'px-4 py-2 text-sm font-medium'}
      ${variant === 'outline' ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50' : ''}
      ${variant === 'ghost' ? 'text-orange-600 hover:bg-orange-100' : ''}
      ${(variant === 'default' || !variant) ? 'bg-orange-600 text-white hover:bg-orange-700' : ''}
      ${className}`}
  >
    {children}
  </button>
);

// Main Filters Component
export default function GalleryFilters({
  categories = [],
  selectedCategory,
  setSelectedCategory,
  selectedTags,
  setSelectedTags,
  searchQuery,
  setSearchQuery,
  viewMode,
  setViewMode,
  allTags,
}) {
  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const clearFilters = () => {
    setSelectedCategory("all");
    setSelectedTags([]);
    setSearchQuery("");
  };

  const hasActiveFilters = selectedCategory !== "all" || selectedTags.length > 0 || searchQuery;

  // FIXED: Convert string categories to options
  const categoryOptions = [
    { id: 'all', name: 'All Categories' },
    ...(categories || []).map((cat) => ({ id: cat, name: cat }))
  ];

  return (
    <div className="space-y-6">
      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search artworks, or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 h-12 border border-gray-300 rounded-md focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("grid")}
            className={viewMode === "grid" ? "bg-orange-600 hover:bg-orange-700" : ""}
          >
            <Grid className="w-5 h-5" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("list")}
            className={viewMode === "list" ? "bg-orange-600 hover:bg-orange-700" : ""}
          >
            <List className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Category Filter - FIXED */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Category</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full h-12 border border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500 bg-white text-sm"
        >
          {categoryOptions.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Tag Filters */}
      {(allTags || []).length > 0 && (
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Filter by Tags</label>
          <div className="flex flex-wrap gap-2">
            {allTags.map((tag) => (
              <Badge
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`cursor-pointer transition-all border
                  ${
                    selectedTags.includes(tag)
                      ? "bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`
                }
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <span className="text-sm text-gray-600 font-medium">Active filters:</span>
          {selectedCategory !== "all" && (
            <Badge className="gap-2 bg-gray-200 text-gray-800 border-gray-300">
              {categoryOptions.find(c => c.id === selectedCategory)?.name || selectedCategory}
              <X
                className="w-3 h-3 cursor-pointer hover:text-red-500"
                onClick={() => setSelectedCategory("all")}
              />
            </Badge>
          )}
          {selectedTags.map((tag) => (
            <Badge key={tag} className="gap-2 bg-gray-200 text-gray-800 border-gray-300">
              {tag}
              <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => toggleTag(tag)} />
            </Badge>
          ))}
          {searchQuery && (
            <Badge className="gap-2 bg-gray-200 text-gray-800 border-gray-300">
              "{searchQuery}"
              <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => setSearchQuery("")} />
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-sm">
            Clear All
          </Button>
        </div>
      )}
    </div>
  );
}